import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { SiteHeader, SiteFooter } from '@/components/SiteLayout';
import { ArrowLeft, Clock, ArrowRight } from 'lucide-react';
import blogPosts from '@/data/blogPosts';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

function RichContent({ content }) {
  // content is an array (static data) or HTML string (from API)
  if (typeof content === 'string' && content.trim()) {
    return <div className="blog-html-content" dangerouslySetInnerHTML={{ __html: content }} />;
  }
  if (Array.isArray(content)) {
    return content.map((block, i) => {
      if (block.type === 'paragraph') return <p key={i} className="text-[15px] text-muted-foreground leading-relaxed mb-5">{block.text}</p>;
      if (block.type === 'heading' && block.level === 2) return <h2 key={i} className="text-xl md:text-2xl font-bold text-foreground mt-10 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>{block.text}</h2>;
      if (block.type === 'heading' && block.level === 3) return <h3 key={i} className="text-lg font-semibold text-foreground mt-8 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{block.text}</h3>;
      if (block.type === 'list') return (
        <ul key={i} className="list-disc pl-6 space-y-2 mb-5">
          {block.items.map((item, j) => <li key={j} className="text-[15px] text-muted-foreground leading-relaxed">{item}</li>)}
        </ul>
      );
      if (block.type === 'comparison') return (
        <div key={i} className="overflow-x-auto my-6">
          <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
            <thead><tr className="bg-muted/50">
              <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Feature</th>
              <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Traditional</th>
              <th className="text-left px-4 py-2.5 font-semibold text-primary border-b border-border">Stablecoin</th>
            </tr></thead>
            <tbody>{block.items.map((row, j) => (
              <tr key={j}>
                <td className="px-4 py-2.5 border-b border-border text-foreground font-medium">{row.feature}</td>
                <td className="px-4 py-2.5 border-b border-border text-muted-foreground">{row.traditional}</td>
                <td className="px-4 py-2.5 border-b border-border text-emerald-400 font-medium">{row.stablecoin}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      );
      return null;
    });
  }
  return null;
}

function updateMetaTag(name, content) {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) { tag = document.createElement('meta'); tag.setAttribute('name', name); document.head.appendChild(tag); }
  tag.setAttribute('content', content);
}

function updateOgTag(property, content) {
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) { tag = document.createElement('meta'); tag.setAttribute('property', property); document.head.appendChild(tag); }
  tag.setAttribute('content', content);
}

function updateLinkTag(rel, href) {
  let tag = document.querySelector(`link[rel="${rel}"]`);
  if (!tag) { tag = document.createElement('link'); tag.setAttribute('rel', rel); document.head.appendChild(tag); }
  tag.setAttribute('href', href);
}

const BlogPostPage = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [allBlogs, setAllBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    setNotFound(false);

    // Fetch both blog post and all blogs for the "next post" CTA
    Promise.all([
      axios.get(`${API_URL}/blogs/public/${slug}`).catch(() => null),
      axios.get(`${API_URL}/blogs/public`).catch(() => null),
    ]).then(([postRes, listRes]) => {
      const apiPost = postRes?.data;
      const apiList = listRes?.data || [];

      // Find static content match for rich rendering
      const staticPost = blogPosts.find(p => p.slug === slug);

      if (apiPost) {
        // Merge: use API metadata, prefer API content if non-empty, else static
        const merged = {
          ...apiPost,
          content: apiPost.content && apiPost.content.trim() ? apiPost.content : (staticPost?.content || ''),
          coverGradient: apiPost.cover_gradient,
          readTime: apiPost.read_time,
        };
        setBlog(merged);

        // --- SEO meta tags ---
        const seoTitle = merged.seo_title || merged.title;
        const seoDesc  = merged.meta_description || merged.excerpt || '';
        const pageUrl  = `https://coinpayup.com/blogs/${merged.slug}`;
        const ogImage  = merged.cover_image || 'https://coinpayup.com/shared_img.jpg';

        document.title = `${seoTitle} | CoinPayUp Blog`;

        // Standard
        updateMetaTag('description', seoDesc);

        // Open Graph
        updateOgTag('og:type',        'article');
        updateOgTag('og:title',       seoTitle);
        updateOgTag('og:description', seoDesc);
        updateOgTag('og:url',         pageUrl);
        updateOgTag('og:image',       ogImage);

        // Twitter Card
        updateMetaTag('twitter:card',        'summary_large_image');
        updateMetaTag('twitter:title',       seoTitle);
        updateMetaTag('twitter:description', seoDesc);
        updateMetaTag('twitter:url',         pageUrl);
        updateMetaTag('twitter:image',       ogImage);

        if (merged.canonical_url) updateLinkTag('canonical', merged.canonical_url);
      } else if (staticPost) {
        setBlog({ ...staticPost, coverGradient: staticPost.coverGradient, readTime: staticPost.readTime });
        document.title = `${staticPost.title} | CoinPayUp Blog`;
      } else {
        setNotFound(true);
      }

      // Build the all-blogs list (API first, fallback to static)
      if (apiList.length > 0) {
        setAllBlogs(apiList.map(b => ({ slug: b.slug, title: b.title, category: b.category, cover_gradient: b.cover_gradient })));
      } else {
        setAllBlogs(blogPosts.map(b => ({ slug: b.slug, title: b.title, category: b.category, cover_gradient: b.coverGradient })));
      }
    }).finally(() => setLoading(false));
  }, [slug]);

  // Cleanup: reset meta tags to site defaults on unmount
  useEffect(() => {
    return () => {
      document.title = 'CoinPayUp | Crypto Payments Gateway for Your Website & Store';
      updateMetaTag('description', 'Accept instant crypto payments on your website or online store with CoinPayUp.');
      updateOgTag('og:type',        'website');
      updateOgTag('og:title',       'Accept Instant Crypto Payments for Websites & Online Stores');
      updateOgTag('og:description', 'Accept USDT & other cryptocurrencies instantly. CoinPayUp verifies blockchain payments automatically.');
      updateOgTag('og:url',         'https://coinpayup.com/');
      updateOgTag('og:image',       'https://coinpayup.com/shared_img.jpg');
      updateMetaTag('twitter:title',       'Accept Instant Crypto Payments for Websites & Online Stores');
      updateMetaTag('twitter:description', 'Enable instant crypto checkout on your website or online store with automatic blockchain verification.');
      updateMetaTag('twitter:url',         'https://coinpayup.com/');
      updateMetaTag('twitter:image',       'https://coinpayup.com/shared_img.jpg');
    };
  }, []);

  // JSON-LD Article schema — injected separately once blog data is loaded
  useEffect(() => {
    if (!blog || !blog.slug || !blog.created_at) return;
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": blog.title,
      "description": blog.meta_description || blog.excerpt || '',
      "url": `https://coinpayup.com/blogs/${blog.slug}`,
      "datePublished": blog.created_at ? blog.created_at.substring(0, 10) : '',
      "dateModified": blog.updated_at ? blog.updated_at.substring(0, 10) : '',
      "author": { "@type": "Organization", "name": "CoinPayUp", "url": "https://coinpayup.com" },
      "publisher": { "@type": "Organization", "name": "CoinPayUp", "url": "https://coinpayup.com", "logo": { "@type": "ImageObject", "url": "https://coinpayup.com/favicon.ico" } },
      "mainEntityOfPage": { "@type": "WebPage", "@id": `https://coinpayup.com/blogs/${blog.slug}` }
    };
    const ldEl = document.createElement('script');
    ldEl.type = 'application/ld+json';
    ldEl.setAttribute('data-schema', 'blog-article');
    ldEl.text = JSON.stringify(articleSchema);
    document.head.appendChild(ldEl);
    // Cleanup: remove JSON-LD script on unmount / slug change
    return () => ldEl.remove();
  }, [blog]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background" data-testid="blog-post-page">
        <SiteHeader />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center text-muted-foreground text-sm">Loading...</div>
        <SiteFooter />
      </div>
    );
  }

  if (notFound) return <Navigate to="/blogs" replace />;

  const currentIdx = allBlogs.findIndex(b => b.slug === slug);
  const nextPost = allBlogs[(currentIdx + 1) % allBlogs.length];

  return (
    <div className="min-h-screen bg-background" data-testid="blog-post-page">
      <SiteHeader />

      {/* Hero */}
      {blog.cover_image ? (
        <div className="border-b border-border relative">
          <div className="absolute inset-0 overflow-hidden">
            <img src={blog.cover_image} alt={blog.title} className="w-full h-full object-cover opacity-25" />
            <div className="absolute inset-0 bg-gradient-to-b from-background/40 to-background" />
          </div>
          <div className="relative max-w-3xl mx-auto px-6 py-12 md:py-16">
            <Link to="/blogs" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6" data-testid="back-to-blog">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to Blog
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-primary/10 text-primary border border-primary/20">{blog.category}</span>
              <span className="flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3 w-3" />{blog.readTime || blog.read_time}</span>
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{blog.title}</h1>
          </div>
          {/* Full cover image below header */}
          <div className="max-w-3xl mx-auto px-6 pb-0">
            <img src={blog.cover_image} alt={blog.title} className="w-full rounded-t-xl object-cover max-h-80" />
          </div>
        </div>
      ) : (
        <div className={`border-b border-border bg-gradient-to-br ${blog.coverGradient || blog.cover_gradient || 'from-indigo-600/20 to-cyan-600/20'}`}>
          <div className="max-w-3xl mx-auto px-6 py-12 md:py-16">
            <Link to="/blogs" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6" data-testid="back-to-blog">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to Blog
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-primary/10 text-primary border border-primary/20">{blog.category}</span>
              <span className="flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3 w-3" />{blog.readTime || blog.read_time}</span>
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{blog.title}</h1>
          </div>
        </div>
      )}

      {/* Content */}
      <article className="max-w-3xl mx-auto px-6 py-12" data-testid="blog-content">
        <RichContent content={blog.content} />
      </article>

      {/* Next Post CTA */}
      {nextPost && nextPost.slug !== slug && (
        <div className="border-t border-border">
          <div className="max-w-3xl mx-auto px-6 py-12">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">Next article</p>
            <Link to={`/blogs/${nextPost.slug}`} className="group flex items-center justify-between p-5 rounded-xl border border-border hover:border-primary/40 hover:bg-muted/30 transition-all" data-testid="next-post-link">
              <div>
                <span className="text-xs text-muted-foreground">{nextPost.category}</span>
                <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors mt-0.5" style={{ fontFamily: "'Outfit', sans-serif" }}>{nextPost.title}</h3>
              </div>
              <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 ml-4" />
            </Link>
          </div>
        </div>
      )}

      <SiteFooter />
    </div>
  );
};

export default BlogPostPage;
